import * as React from "react";
import {
  Box,
  Chip,
  Button,
  List,
  Divider,
  ListItem,
  ListItemText,
  Stack,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CancelIcon from "@mui/icons-material/Cancel";
import TitleIcon from "@mui/icons-material/Title";
import BusinessIcon from "@mui/icons-material/Business";
import DescriptionIcon from "@mui/icons-material/Description";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import StraightenIcon from "@mui/icons-material/Straighten";
import BuildIcon from "@mui/icons-material/Build";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Project, ProjectStatus } from "../../types/project";
import MapPreview from "../../components/MapPreview";
import { nl2br } from "../../utils/helper";
import ChipProjectStatus from "../../components/ChipProjectStatus";
import { decode } from "html-entities";
import { useGetProjectByIdQuery } from "../../services/project.service";
import { GlassDrawer, OrangeLinearProgress } from "../../components/global";

interface ProjectDrawerProps {
  data: Project;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  forceRefresh?: boolean;
}

export default function ProjectDrawer(props: ProjectDrawerProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: dataProp, open, setOpen, forceRefresh = false } = props;
  const [data, setData] = React.useState(dataProp);

  const {
    data: dataApi,
    isError,
    isLoading,
    isFetching,
  } = useGetProjectByIdQuery(dataProp?.id!, {
    skip: !forceRefresh || !dataProp,
    refetchOnMountOrArgChange: true,
  });

  React.useEffect(() => {
    if (!!dataApi && !isError) setData(dataApi);
    else setData(dataProp);
  }, [dataApi, dataProp, isError]);

  const [lng, lat] = React.useMemo(() => {
    if (!!data?.customer) {
      return data?.customer?.coordinate?.coordinates || [];
    } else if (!!data?.location) {
      return data?.location?.coordinate?.coordinates || [];
    } else return [];
  }, [data]);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <GlassDrawer
      anchor="right"
      open={open}
      onClose={handleClose}
      title={`#${data?.id} ${data?.title}`}
      contentProps={{
        sx: {
          gap: 0,
        },
      }}
    >
      {isMobile && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <IconButton onClick={handleClose} size="small">
            <ClearIcon />
          </IconButton>
        </Box>
      )}
      {isLoading || isFetching ? <OrangeLinearProgress position="relative" sx={{ mb: 2 }} /> : null}
      <Box sx={{ mb: 2 }}>
        <MapPreview
          lat={lat}
          lng={lng}
          height="300px"
          radius={data?.mile_radius}
        />
      </Box>
      <List>
        <Box sx={{ ml: 1, mt: 2, mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ChipProjectStatus
              status={data?.status}
              size="medium"
              sx={{ fontSize: 14 }}
            />
            <Chip
              label={data?.type == "hourly" ? "HOURLY" : "FIXED PRICE"}
              size="medium"
              sx={{ fontSize: 14 }}
            />
          </Stack>
        </Box>
        {data?.status == ProjectStatus.CANCELED && (
          <ListItem>
            <ListItemText
              primary={
                <Typography sx={{ color: "#F4A259", fontWeight: 600 }}>
                  Cancel Reason
                </Typography>
              }
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <CancelIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                  <Typography component="span">{data.canceled_reason || "n/a"}</Typography>
                </Box>
              }
            />
          </ListItem>
        )}
        <Divider />
        <ListItem>
          <ListItemText
            primary={
              <Typography sx={{ color: "#F4A259", fontWeight: 600 }}>
                ID/Title
              </Typography>
            }
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <TitleIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                <Typography component="span" sx={{ fontSize: 18, fontWeight: "500" }}>
                  #{data?.id} {data?.title}
                </Typography>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primary={
              <Typography sx={{ color: "#F4A259", fontWeight: 600 }}>
                Company
              </Typography>
            }
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <BusinessIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                <Typography component="span">{data?.company?.name}</Typography>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primary={
              <Typography sx={{ color: "#F4A259", fontWeight: 600 }}>
                Scope of Work
              </Typography>
            }
            secondary={
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 0.5 }}>
                <DescriptionIcon sx={{ color: "text.secondary", fontSize: 18, mt: 0.5 }} />
                <Box component="span">{nl2br(decode(data?.description))}</Box>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primary={
              <Typography sx={{ color: "#F4A259", fontWeight: 600 }}>
                Start At
              </Typography>
            }
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <ScheduleIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                <Typography component="span">
                  {dayjs(data?.start_date).format("MMM DD, YYYY hh:mm A")}
                </Typography>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        {!!data?.location && (
          <ListItem>
            <ListItemText
              primary={
                <Typography sx={{ color: "#F4A259", fontWeight: 600 }}>
                  Location Address
                </Typography>
              }
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <LocationOnIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                  <Typography component="span">
                    {data?.location.address_1}, {data?.location.city}, {data?.location.zip}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        )}
        {!!data?.customer && (
          <ListItem>
            <ListItemText
              primary={
                <Typography sx={{ color: "#F4A259", fontWeight: 600 }}>
                  Client Address
                </Typography>
              }
              secondary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                  <HomeIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                  <Typography component="span">
                    {data?.customer?.physical_address_1}, {data?.customer?.city}, {data?.customer?.zip}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        )}
        <Divider />
        <ListItem>
          <ListItemText
            primary={
              <Typography sx={{ color: "#F4A259", fontWeight: 600 }}>
                Approximate Hours To Complete
              </Typography>
            }
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <AccessTimeIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                <Typography component="span">{data?.approximate_hours_to_complete} hr</Typography>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primary={
              <Typography sx={{ color: "#F4A259", fontWeight: 600 }}>
                Mile Radius
              </Typography>
            }
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <StraightenIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                <Typography component="span">{data?.mile_radius} miles</Typography>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primary={
              <Typography sx={{ color: "#F4A259", fontWeight: 600 }}>
                Tool Required
              </Typography>
            }
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <BuildIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                <Typography component="span">{!!data?.tool_required ? "Yes" : "No"}</Typography>
              </Box>
            }
          />
        </ListItem>
      </List>
      <Box display="flex" justifyContent="center" sx={{ mt: 5 }}>
        <Button
          variant="contained"
          onClick={() => navigate(`/jobs/${data?.id}`)}
          sx={{
            backgroundColor: "#F4A259",
            color: "#ffffff",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#E8944A",
            },
          }}
        >
          More Detail
        </Button>
      </Box>
    </GlassDrawer>
  );
}
